import { titleize } from "helpers/string";
import { camelCase, get } from "lodash";
import { IssueDetail, IssueSummary, TIMING_ISSUE_TYPES } from "models/issue";
import { ComponentWithAs, IconProps } from "@chakra-ui/react";
import CaminoBuddyIcon from "components/Icon/CaminoBuddy";
import CaminoManagerIcon from "components/Icon/CaminoManager";
import CaminoNewHireIcon from "components/Icon/CaminoNewHire";
import CaminoPeopleTeamIcon from "components/Icon/CaminoPeopleTeamIcon";
import { Company } from "models/company";
import { PersonalizationTokenRoleName } from "models/personalizationToken";
import { User } from "features/User/models";
import { IconType } from "react-icons";
import { HiOutlineOfficeBuilding } from "react-icons/hi";
import { ActionV2, ActionSummary } from "features/Resources/Shared/models";
import { actionDetailByPrefixQueryArgs } from "features/Resources/Shared/hooks";
import { extractAction, isActionTimingValid, isImmediate, primaryTriggerDate } from "features/Resources/Shared/helpers";
import { NewHireJourney } from "models/v2/newHireJourney";
import { isWithinNext3Days } from "helpers/time";
import { UNSENT_STATUSES } from "features/Resources/Shared/constants";

const ROLE_NAME_FIELDS = ["buddyId", "peopleTeamContactId", "managerId"];

export function toRoleFieldName(roleName: string | undefined) {
  if (!roleName) {
    return undefined;
  }
  const roleNameField = `${camelCase(roleName)}Id`;
  if (!ROLE_NAME_FIELDS.includes(roleNameField)) {
    throw new Error(`Invalid role name: ${roleName}`);
  }
  return roleNameField;
}

export function toRoleFieldNameSafe(roleName: string | undefined) {
  try {
    return toRoleFieldName(roleName);
  } catch {
    return undefined;
  }
}

const EMAIL_TYPE_FIELDS = ["workEmail", "personalEmail"];

export function toEmailFieldName(emailType: string | undefined) {
  if (!emailType) {
    return "";
  }
  const emailTypeField = camelCase(emailType);
  if (!EMAIL_TYPE_FIELDS.includes(emailTypeField)) {
    throw new Error(`Invalid email type field name: ${emailTypeField}`);
  }
  return emailTypeField;
}

export function isTimingIssue(issue: IssueSummary | IssueDetail) {
  return issue && TIMING_ISSUE_TYPES.includes(issue.issueType);
}

export function hasNonTimingIssues(action: ActionV2 | undefined) {
  return (action?.issues || []).some(
    (issue: IssueSummary | IssueDetail) => !TIMING_ISSUE_TYPES.includes(issue.issueType),
  );
}

export function excludeTimingIssues(issues: Array<IssueSummary | IssueDetail> | undefined) {
  return (issues || []).filter((issue: IssueSummary | IssueDetail) => !TIMING_ISSUE_TYPES.includes(issue.issueType));
}

export function actionNonTimingIssues(action: ActionV2 | undefined) {
  return excludeTimingIssues(action?.issues || []);
}

export function actionsWithNonTimingIssues(actions: ActionV2[]) {
  return actions.filter((action) => hasNonTimingIssues(action));
}

export function reorderTimingIssues(issues: IssueSummary[]): IssueSummary[] {
  const { timingIssues, nonTimingIssues } = issues.reduce<{
    timingIssues: IssueSummary[];
    nonTimingIssues: IssueSummary[];
  }>(
    (acc, issue) => {
      if (TIMING_ISSUE_TYPES.includes(issue.issueType)) {
        acc.timingIssues.push(issue);
      } else {
        acc.nonTimingIssues.push(issue);
      }
      return acc;
    },
    { timingIssues: [], nonTimingIssues: [] },
  );

  return [...nonTimingIssues, ...timingIssues];
}

export function isRoleSet(roleName: string | undefined, newHireJourney: NewHireJourney | undefined) {
  if (!roleName || !newHireJourney) {
    return false;
  }
  const roleFieldName = toRoleFieldName(roleName) as keyof NewHireJourney;
  return !!newHireJourney[roleFieldName];
}

export function displayRoleName(issue: IssueSummary | IssueDetail) {
  return issue?.context?.roleName ? titleize(issue?.context?.roleName) : "Role";
}

interface AttributeValueArgs {
  company?: Company;
  newHireJourney?: NewHireJourney;
  user?: User;
  roleName: PersonalizationTokenRoleName;
  attribute: string;
}

export function attributeValue({
  company,
  newHireJourney,
  user,
  roleName,
  attribute,
}: AttributeValueArgs): string | undefined {
  const camelCaseAttribute = camelCase(attribute);

  if (!roleName) {
    return undefined;
  }
  switch (roleName) {
    case "recipient":
    case "assignee":
    case "new_hire":
      return get(user, camelCaseAttribute);
    case "manager":
      return get(user, camelCaseAttribute);
    case "buddy":
      return get(user, camelCaseAttribute);
    case "people_team_contact":
      return get(user, camelCaseAttribute);
    case "new_hire_journey":
      return get(newHireJourney, camelCaseAttribute);
    case "company":
      return get(company, camelCaseAttribute);
    default:
      console.error(
        `Cannot find attribute ${camelCaseAttribute} value - Invalid roleName ${roleName}: userId ${user?.id}, newHireJourneyId ${newHireJourney?.id}, companyId ${company?.id}`,
      );
      return undefined;
  }
}

interface AttributeIconArgs {
  company?: Company;
  newHireJourney?: NewHireJourney;
  user?: User;
  roleName: PersonalizationTokenRoleName;
}

type returnIconType = IconType | ((props: IconProps) => JSX.Element) | ComponentWithAs<"svg", IconProps> | undefined;

export function attributeIcon({ company, newHireJourney, user, roleName }: AttributeIconArgs): returnIconType {
  if (!roleName) {
    return { icon: undefined, value: undefined };
  }
  switch (roleName) {
    case "recipient":
    case "assignee":
    case "new_hire":
      return CaminoNewHireIcon;
    case "manager":
      return CaminoManagerIcon;
    case "buddy":
      return CaminoBuddyIcon;
    case "people_team_contact":
      return CaminoPeopleTeamIcon;
    case "new_hire_journey":
      return CaminoNewHireIcon;
    case "company":
      return HiOutlineOfficeBuilding;
    default:
      console.error(
        `Cannot find attribute Icon - Invalid roleName ${roleName}: userId ${user?.id}, newHireJourneyId ${newHireJourney?.id}, companyId ${company?.id}`,
      );
      return undefined;
  }
}

export function timingIssueQueryArgs(action: ActionV2) {
  return {
    ...actionDetailByPrefixQueryArgs(action?.prefixId),
    select: (returnAction: ActionV2) => isActionTimingValid(returnAction),
  };
}

export function hasUnsentActions(actions: ActionV2[] | ActionSummary[]) {
  return actions?.some((action) =>
    UNSENT_STATUSES.includes(
      (action as ActionSummary)?.actionType ? (action as ActionSummary)?.action?.status : (action as ActionV2)?.status,
    ),
  );
}

export function timingIssueAllActionsSentOrSkipped(issue: IssueDetail | undefined) {
  return !hasUnsentActions(issue?.actions || []);
}

export function timingIssueActionResolved(action: ActionV2 | ActionSummary) {
  const resolvedAction = (action as ActionSummary)?.actionType
    ? (action as ActionSummary)?.action
    : (action as ActionV2);
  return isActionTimingValid(resolvedAction as ActionV2);
}

export function dedupeMissingSlackUsers(issues: IssueSummary[] | undefined) {
  const userIssueMap = new Map<string, { hasMessageIssue: boolean; hasMissingSlackMention: boolean }>();

  // Collect issue types per user_id
  (issues || []).forEach((issue) => {
    const userId = issue?.context?.userId;
    if (!userId) return;

    const existing = userIssueMap.get(userId) || { hasMessageIssue: false, hasMissingSlackMention: false };

    if (issue.issueType === "missing_message_service_user") {
      existing.hasMessageIssue = true;
    }

    if (issue.issueType === "missing_attribute" && issue?.context?.attribute === "slack_mention") {
      existing.hasMissingSlackMention = true;
    }

    userIssueMap.set(userId, existing);
  });

  // Filter based on collected data
  return (issues || []).filter((issue) => {
    const userId = issue?.context?.userId;
    if (!userId) return true;

    const { hasMessageIssue, hasMissingSlackMention } = userIssueMap.get(userId) || {};

    // Keep only `missing_message_service_user` when both exist
    if (hasMessageIssue && hasMissingSlackMention) {
      return issue.issueType === "missing_message_service_user";
    }

    // Otherwise, keep all issues as they are
    return true;
  });
}

export function hasPendingIssues(action: ActionSummary | ActionV2 | undefined, isPending = false) {
  if (!action) {
    return false;
  }
  const triggerDate = primaryTriggerDate(action);
  const resolvedAction = extractAction(action);
  return isPending && !!resolvedAction?.issueIds.length && (isImmediate(action) || isWithinNext3Days(triggerDate));
}
